import './EditPagePresetModal.scss';
import cn from 'classnames';
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Form, Modal } from 'react-bootstrap';
import { MdAdd, MdClose, MdEdit } from 'react-icons/md';
import {
  getDefaultSparkChartMetrics,
  MASTER_COLUMN_MAP
} from '@blisspointmedia/bpm-types/dist/Columns';
import { KpiPreset, Metric } from '@blisspointmedia/bpm-types/dist/CrossChannel';
import { MASTER_METRIC_MAP } from '@blisspointmedia/bpm-types/dist/Metrics';
import {
  DateInfo,
  GlobalOptions,
  MetricsPagePreset
} from '@blisspointmedia/bpm-types/dist/MetricsPage';
import { PresetIDGroups as TablePresetIDGroups } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { FilterPreset } from '@blisspointmedia/bpm-types/dist/pgTables/FilterPreset';
import {
  CustomPresetRangeState,
  CustomPresetState,
  INIT_CUSTOM_PRESET_STATE,
  RangeErrorPreset,
  ValidCustomPresetState
} from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { LINEAR, refactorEnabled } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { AdminLockToggle } from '@Components/AdminLockToggle/AdminLockToggle';
import { Button } from '@Components/Button';
import { DateInputField } from '@Components/DatePicker/DateInputField/DateInputField';
import { DPPresets } from '@Components/DatePicker/DPPresets/DPPresets';
import { Appearance, Design, Placement, Size } from '@Components/designConstants';
import { Dropdown, DropdownOption } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { useAppSelector } from '@src/redux';
import * as UserRedux from '@src/redux/user';
import {
  getInvalidAnalyticsDetails,
  getInvalidGranularityDetailsFromComparingPresets,
  InvalidGranularityDetails,
  makeInvalidDimMsg
} from '@src/utils/columnMetricSystem/configUtils';
import { DateRange, snapToMonday } from '@src/utils/date-utils';
import { useExperimentFlag } from '@src/utils/experiment-context';
import { useExperimentalAnalyticsCols } from '@src/utils/hooks/useExperimentalAnalyticsCols';
import { STANDARD_GROUP_NAME } from '@src/utils/performance-utils';
import { makeDropdownOptions } from './Components/DeliverySnapshot';
import { SourceType } from './Components/SourcePicker';
import {
  DEFAULT_SPARKCHARTS_MAP,
  DELIVERY_METRIC_OPTIONS,
  METRIC_OPTIONS,
  PERFORMANCE_METRIC_OPTIONS
} from './cross-channel-constants';

// SingleChannel's Delivery tab (this modal is shared by SingleChannel and CrossChannel pages, gated
// by the `menuTabs` prop) reads its metric options from `SingleChannel/Delivery/SingleChannelDeliveryChart`,
// which hasn't been migrated to tinuiti-frontend yet. That component is a large, unrelated chart —
// porting it just to satisfy this prop's type is out of scope for this modal. The type and its
// default are trivial (an array of `{ label, value }`, optionally flagged `isSpend`), so they are
// inlined here rather than fabricated as an import from a file that doesn't exist.
interface DeliveryMetricOption {
  isSpend?: boolean;
  label: string;
  value: string;
}
const DEFAULT_DELIVERY_METRICS_OPTIONS: DeliveryMetricOption[] = [
  { label: 'Spend', isSpend: true, value: 'cost' },
  { label: 'Impressions', value: 'count' }
];

type DateIncrement = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

const DEFAULT_MENU_TABS = [
  'Dates',
  'KPI, etc.',
  'Filters',
  'Overview',
  'Delivery',
  'Metrics Table',
  'Preset Group'
];

// Fallback spark charts when the mediatype isn't present in DEFAULT_SPARKCHARTS_MAP.
const DEFAULT_SPARKCHARTS = ['spend', 'impressions', 'volume', 'roas'];

// A relative-date field is either a preset name (string), a custom range object, or absent.
// DPPresets only wants to know: which preset name is active, or "Custom" if a range object is
// set, or nothing selected.
const getSelectedPresetLabel = (
  relativeDateInfo: DateInfo['relative_date_info'] | DateInfo['comparison_relative_date_info']
): string | null => {
  if (typeof relativeDateInfo === 'string') {
    return relativeDateInfo;
  }
  return relativeDateInfo ? 'Custom' : null;
};

interface GlobalOptionsPickers {
  audience?: string[];
  kpi?: { options: { label: string; value: string }[] };
  lag?: string[];
  source?: { label: string; value: string }[];
  impsBetween?: boolean;
}

interface EditPagePresetModalProps {
  dateIncrementOptions?: DateIncrement[];
  deliveryDimensionMap?: Record<string, string>;
  deliveryMetricOptions?: DeliveryMetricOption[];
  filterPresets?: FilterPreset[];
  funnelPresets?: KpiPreset[];
  globalOptionsPickers: GlobalOptionsPickers;
  groups?: string[];
  mediatype?: string;
  menuTabs?: string[];
  // Consumer-supplied tabs rendered when their label is the selected tab. The label must also be
  // included in `menuTabs` so it appears in the tab strip. Used by pages (e.g. Pacing) that need
  // page-specific settings the shared tabs don't cover.
  extraTabs?: { label: string; content: ReactElement }[];
  // When true, the Dates tab shows only the Primary Period (no Comparison Period or Date Increments).
  // Used by pages without a comparison-period concept (e.g. Pacing).
  singleDatePeriod?: boolean;
  onHide?: () => void;
  pagePresetChanges: MetricsPagePreset;
  saveAndApplyOnClick: (pagePresetChanges: MetricsPagePreset) => void;
  setPagePresetChanges: Dispatch<SetStateAction<MetricsPagePreset>>;
  sparkChartOptions?: { label: string; value: string }[];
  tablePresets?: TablePresetIDGroups[];
  // which dimensions are currently restricted because of the current metrics table selection in page preset changes
  // Ex. Metrics table with dimension "retailer" means filter preset with dimension "keyword" are invalid
  incompatibleTableDimsFromTableChanges?: InvalidGranularityDetails;
  // which dimensions are currently restricted because of the current filter selection in page preset changes
  // Ex. Filter with dimension "retailer" means metrics table preset with dimension "keyword" are invalid
  incompatibleFilterDimsFromFilterChanges?: InvalidGranularityDetails;
  // same as the above but mapped for delivery which only reads pretty names in this component
  // Ex. Filter with dimension "retailer" means delivery dimension "keyword" is invalid
  incompatibleDeliveryDimsFromFilterChanges?: InvalidGranularityDetails;
  // which filter dimension are currently restricted because of the current delivery dimension selection in page preset changes
  // Ex. Delivery dimension "retailer" means filter preset including dimension "keyword" is invalid
  incompatibleFilterDimsFromDeliveryChanges?: InvalidGranularityDetails;
}

interface ModalHeaderProps {
  pagePresetChanges: MetricsPagePreset;
  newName: string | undefined;
  setNewName: Dispatch<SetStateAction<string | undefined>>;
  adminLock: boolean;
  isAdmin: boolean;
  internalOnly: boolean;
  setPagePresetChanges: Dispatch<SetStateAction<MetricsPagePreset>>;
}

const ModalHeader = ({
  pagePresetChanges,
  newName,
  setNewName,
  adminLock,
  isAdmin,
  internalOnly,
  setPagePresetChanges
}: ModalHeaderProps) => (
  <Modal.Header>
    <div className='title-container'>
      <Modal.Title>Page Preset:</Modal.Title>
      <div className='page-preset-name-container'>
        {newName !== undefined ? (
          <Form.Control
            className='new-name-input'
            onChange={e => setNewName(e.target.value)}
            placeholder='New Preset Name'
            value={newName}
          />
        ) : (
          <div className='page-preset-name'>
            {pagePresetChanges.temporary ? 'Temporary Preset' : pagePresetChanges.name}
          </div>
        )}
        {pagePresetChanges.name !== 'Default' &&
          (newName === undefined ? (
            <IconButton
              appearance={Appearance.EMPTY}
              onClick={() => setNewName(pagePresetChanges.name)}
            >
              <MdEdit />
            </IconButton>
          ) : (
            <IconButton
              appearance={Appearance.EMPTY}
              design={Design.NEUTRAL}
              onClick={() => setNewName(undefined)}
            >
              <MdClose />
            </IconButton>
          ))}
      </div>
    </div>
    <AdminLockToggle
      checked={adminLock}
      onChange={() =>
        setPagePresetChanges({
          ...pagePresetChanges,
          globalOptions: { ...pagePresetChanges.globalOptions, admin_lock: !adminLock }
        })
      }
      className='internal-only-toggle'
      disabled={!isAdmin}
    />
    <ToggleSwitch
      checked={internalOnly}
      className='internal-only-toggle'
      design={Design.BRAND}
      label='Internal-only'
      onChange={() =>
        setPagePresetChanges({
          ...pagePresetChanges,
          globalOptions: { ...pagePresetChanges.globalOptions, role: internalOnly ? 10 : 1 }
        })
      }
    />
  </Modal.Header>
);

interface TabSelectorProps {
  menuTabs: string[];
  presetGroup: string | undefined;
  selectedTab: string;
  setSelectedTab: Dispatch<SetStateAction<string>>;
}

const TabSelector = ({ menuTabs, presetGroup, selectedTab, setSelectedTab }: TabSelectorProps) => (
  <div className='tab-selector'>
    {menuTabs
      .filter(tab => tab !== 'Preset Group' || presetGroup !== STANDARD_GROUP_NAME)
      .map(tab => (
        <InteractiveDiv
          key={`edit-page-preset-modal-tab-${tab}`}
          className={cn('tab', { active: selectedTab === tab })}
          onClick={() => setSelectedTab(tab)}
        >
          {tab === 'Cross-Channel Overview' ? 'Overview' : tab}
        </InteractiveDiv>
      ))}
  </div>
);

interface ModalFooterProps {
  disableApply: boolean;
  newName: string | undefined;
  onHide: () => void;
  pagePresetChanges: MetricsPagePreset;
  saveAndApplyOnClick: (pagePresetChanges: MetricsPagePreset) => void;
}

const ModalFooter = ({
  disableApply,
  newName,
  onHide,
  pagePresetChanges,
  saveAndApplyOnClick
}: ModalFooterProps) => (
  <Modal.Footer>
    <Button
      appearance={Appearance.OUTLINED}
      design={Design.NEUTRAL}
      className='cancel-button'
      onClick={onHide}
    >
      Cancel
    </Button>
    <Button
      appearance={Appearance.OUTLINED}
      className='preview-button'
      design={Design.NEUTRAL}
      disabled={disableApply}
      onClick={() =>
        saveAndApplyOnClick({
          ...pagePresetChanges,
          temporary: true,
          // A temp preview of a not-yet-saved preset has no id yet. `MetricsPagePreset.id` is
          // typed as a required `number` because saved presets always have one, but the backend
          // treats a missing id as "create new" for temporary/unsaved previews.
          id: pagePresetChanges.temporary ? pagePresetChanges.id : undefined
        } as MetricsPagePreset)
      }
    >
      Temp Preview
    </Button>
    <Button
      className='save-and-apply-button'
      disabled={disableApply}
      onClick={() =>
        saveAndApplyOnClick({ ...pagePresetChanges, name: newName ?? pagePresetChanges.name })
      }
    >
      Save and Apply
    </Button>
  </Modal.Footer>
);

export const EditPagePresetModal = ({
  dateIncrementOptions,
  deliveryDimensionMap = {},
  deliveryMetricOptions = DEFAULT_DELIVERY_METRICS_OPTIONS,
  filterPresets = [],
  funnelPresets = [],
  globalOptionsPickers,
  groups = [],
  mediatype,
  menuTabs = DEFAULT_MENU_TABS,
  extraTabs = [],
  singleDatePeriod = false,
  onHide = () => {},
  pagePresetChanges,
  saveAndApplyOnClick,
  setPagePresetChanges,
  sparkChartOptions = [],
  tablePresets = [],
  incompatibleTableDimsFromTableChanges = {},
  incompatibleFilterDimsFromFilterChanges = {},
  incompatibleDeliveryDimsFromFilterChanges = {},
  incompatibleFilterDimsFromDeliveryChanges = {}
}: EditPagePresetModalProps) => {
  const shouldEnableImpsBetweenToggle = useExperimentFlag('enableImpsBetweenToggle');
  const shouldEnableByResponseDate = useExperimentFlag('enableByResponseDate');
  const expAnalyticsCols = useExperimentalAnalyticsCols();
  const [selectedTab, setSelectedTab] = useState(menuTabs[0] ?? 'Dates');
  const [newName, setNewName] = useState<string | undefined>();
  const [hasNewGroupName, setHasNewGroupName] = useState(false);
  const [showGroupsDropdownMenu, setShowGroupsDropdownMenu] = useState<boolean | undefined>(
    undefined
  );
  const adminLock = pagePresetChanges.globalOptions?.admin_lock ?? false;
  const internalOnly = (pagePresetChanges.globalOptions?.role ?? 10) < 2;
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);

  const [startCustomPreset, setStartCustomPreset] = useState<CustomPresetState>({
    ...INIT_CUSTOM_PRESET_STATE,
    ...((pagePresetChanges.dateInfo.relative_date_info as CustomPresetRangeState)?.start ?? {})
  });
  const [endCustomPreset, setEndCustomPreset] = useState<CustomPresetState>({
    ...INIT_CUSTOM_PRESET_STATE,
    ...((pagePresetChanges.dateInfo.relative_date_info as CustomPresetRangeState)?.end ?? {})
  });
  const [startCompCustomPreset, setStartCompCustomPreset] = useState<CustomPresetState>({
    ...INIT_CUSTOM_PRESET_STATE,
    ...((pagePresetChanges.dateInfo.comparison_relative_date_info as CustomPresetRangeState)
      ?.start ?? {})
  });
  const [endCompCustomPreset, setEndCompCustomPreset] = useState<CustomPresetState>({
    ...INIT_CUSTOM_PRESET_STATE,
    ...((pagePresetChanges.dateInfo.comparison_relative_date_info as CustomPresetRangeState)?.end ??
      {})
  });
  const [rangeErrorPreset, setRangeErrorPreset] = useState<RangeErrorPreset>(null);
  const [compRangeErrorPreset, setCompRangeErrorPreset] = useState<RangeErrorPreset>(null);
  const [dateInputs, setDateInputs] = useState<Partial<DateRange>>({
    start: pagePresetChanges.dateInfo.start,
    end: pagePresetChanges.dateInfo.end
  });
  const [comparisonDateInputs, setComparisonDateInputs] = useState<Partial<DateRange>>({
    start: pagePresetChanges.dateInfo.comparison_start,
    end: pagePresetChanges.dateInfo.comparison_end
  });

  const normalizeDate = useCallback(
    (date: string): string => (mediatype === LINEAR ? snapToMonday(date) : date),
    [mediatype]
  );

  const defaultSparkCharts = useMemo(() => {
    const channel = mediatype ?? '';
    if (refactorEnabled(channel)) {
      const defaults = getDefaultSparkChartMetrics(channel);
      if (defaults) {
        return defaults;
      }
    }
    return (
      (channel in DEFAULT_SPARKCHARTS_MAP
        ? DEFAULT_SPARKCHARTS_MAP[channel as keyof typeof DEFAULT_SPARKCHARTS_MAP]
        : undefined) ?? DEFAULT_SPARKCHARTS
    );
  }, [mediatype]);

  const { kpi, audience, lag, source, shouldUseUnequivalizedImpressions } =
    pagePresetChanges.globalOptions ?? {};
  const impsBetween = pagePresetChanges.globalOptions?.impsBetween ?? true;

  const dateIncrementsOptions = useMemo(() => {
    const options = [
      { label: 'Daily', value: 'daily' },
      { label: 'Weekly', value: 'weekly' },
      { label: 'Monthly', value: 'monthly' },
      { label: 'Quarterly', value: 'quarterly' },
      { label: 'Yearly', value: 'yearly' }
    ];
    if (!dateIncrementOptions) {
      return options;
    }
    return options.filter(option => dateIncrementOptions.includes(option.value as DateIncrement));
  }, [dateIncrementOptions]);

  useEffect(() => {
    const currentIncrements = pagePresetChanges.dateInfo.increments;
    if (
      dateIncrementsOptions.length > 0 &&
      currentIncrements &&
      !dateIncrementsOptions.map(option => option.value).includes(currentIncrements)
    ) {
      setPagePresetChanges(p => ({
        ...p,
        dateInfo: { ...p.dateInfo, increments: dateIncrementsOptions[0].value as DateIncrement }
      }));
    }
  }, [dateIncrementsOptions, pagePresetChanges.dateInfo.increments, setPagePresetChanges]);

  const disableApply = useMemo(() => {
    if (adminLock && !isAdmin) {
      return true;
    }
    return (
      pagePresetChanges.dateInfo.relative_date_info === 'Custom' ||
      pagePresetChanges.dateInfo.comparison_relative_date_info === 'Custom'
    );
  }, [adminLock, isAdmin, pagePresetChanges]);

  useEffect(() => {
    setPagePresetChanges(p => {
      const { number: startNum } = startCustomPreset;
      const { number: endNum } = endCustomPreset;
      const shouldSet =
        p.dateInfo.dateType === 'relative' &&
        (p.dateInfo.relative_date_info === 'Custom' ||
          typeof p.dateInfo.relative_date_info === 'object') &&
        typeof startNum === 'number' &&
        typeof endNum === 'number';

      if (shouldSet) {
        return {
          ...p,
          dateInfo: {
            ...p.dateInfo,
            relative_date_info: {
              start: { ...(startCustomPreset as ValidCustomPresetState) },
              end: { ...(endCustomPreset as ValidCustomPresetState) }
            }
          }
        };
      }
      if (typeof p.dateInfo.relative_date_info === 'object') {
        return { ...p, dateInfo: { ...p.dateInfo, relative_date_info: 'Custom' } };
      }
      return p;
    });
  }, [startCustomPreset, endCustomPreset, setPagePresetChanges]);

  useEffect(() => {
    setPagePresetChanges(p => {
      const { number: startNum } = startCompCustomPreset;
      const { number: endNum } = endCompCustomPreset;
      const shouldSet =
        p.dateInfo.comparison_dateType === 'relative' &&
        (p.dateInfo.comparison_relative_date_info === 'Custom' ||
          typeof p.dateInfo.comparison_relative_date_info === 'object') &&
        typeof startNum === 'number' &&
        typeof endNum === 'number';

      if (shouldSet) {
        return {
          ...p,
          dateInfo: {
            ...p.dateInfo,
            comparison_relative_date_info: {
              start: { ...(startCompCustomPreset as ValidCustomPresetState) },
              end: { ...(endCompCustomPreset as ValidCustomPresetState) }
            }
          }
        };
      }
      if (typeof p.dateInfo.comparison_relative_date_info === 'object') {
        return { ...p, dateInfo: { ...p.dateInfo, comparison_relative_date_info: 'Custom' } };
      }
      return p;
    });
  }, [startCompCustomPreset, endCompCustomPreset, setPagePresetChanges]);

  useEffect(() => {
    if (!dateInputs.start || !dateInputs.end) {
      return;
    }
    const start = normalizeDate(dateInputs.start);
    const end = normalizeDate(dateInputs.end);
    setPagePresetChanges(p =>
      p.dateInfo.start === start && p.dateInfo.end === end
        ? p
        : { ...p, dateInfo: { ...p.dateInfo, start, end } }
    );
  }, [setPagePresetChanges, dateInputs, normalizeDate]);

  useEffect(() => {
    if (!comparisonDateInputs.start || !comparisonDateInputs.end) {
      return;
    }
    const start = normalizeDate(comparisonDateInputs.start);
    const end = normalizeDate(comparisonDateInputs.end);
    setPagePresetChanges(p =>
      p.dateInfo.comparison_start === start && p.dateInfo.comparison_end === end
        ? p
        : { ...p, dateInfo: { ...p.dateInfo, comparison_start: start, comparison_end: end } }
    );
  }, [setPagePresetChanges, comparisonDateInputs, normalizeDate]);

  useEffect(() => {
    const options = pagePresetChanges.globalOptions;
    if (
      options &&
      ((!options.audience && audience) ||
        (!options.kpi && kpi) ||
        (!options.lag && lag) ||
        (!options.source && source) ||
        (options.shouldUseUnequivalizedImpressions == null &&
          shouldUseUnequivalizedImpressions != null) ||
        (options.impsBetween == null && impsBetween != null))
    ) {
      setPagePresetChanges(p => ({
        ...p,
        globalOptions: {
          ...p.globalOptions,
          audience: audience ?? 'HH',
          kpi: kpi ?? '',
          lag: lag ?? '7d',
          source: source ?? 'platform',
          shouldUseUnequivalizedImpressions,
          impsBetween
        }
      }));
    }
  }, [
    audience,
    kpi,
    lag,
    pagePresetChanges,
    setPagePresetChanges,
    shouldUseUnequivalizedImpressions,
    source,
    impsBetween
  ]);

  useEffect(() => {
    const { globalOptions } = pagePresetChanges;
    if (
      globalOptions?.tableID != null &&
      tablePresets.length > 0 &&
      !tablePresets.map(preset => preset.id).includes(globalOptions.tableID)
    ) {
      setPagePresetChanges(p => ({
        ...p,
        globalOptions: { ...p.globalOptions, tableID: tablePresets[0].id }
      }));
    }
  }, [pagePresetChanges, setPagePresetChanges, tablePresets]);

  const storedSparkCharts = pagePresetChanges.globalOptions?.sparkCharts;

  const sparkCharts = useMemo(() => {
    // Always copy — this array may get backfilled with missing defaults below, and it must never
    // mutate `pagePresetChanges.globalOptions.sparkCharts` (a prop) in place.
    const currentSparkCharts: string[] = [...(storedSparkCharts ?? defaultSparkCharts)];
    for (let i = 0; i < defaultSparkCharts.length; i += 1) {
      if (!currentSparkCharts[i]) {
        currentSparkCharts[i] = defaultSparkCharts[i];
      }
    }
    return currentSparkCharts;
  }, [defaultSparkCharts, storedSparkCharts]);

  useEffect(() => {
    const needsBackfill = defaultSparkCharts.some((_chart, i) => !storedSparkCharts?.[i]);
    if (needsBackfill) {
      setPagePresetChanges(p => ({
        ...p,
        globalOptions: { ...p.globalOptions, sparkCharts }
      }));
    }
  }, [sparkCharts, defaultSparkCharts, storedSparkCharts, setPagePresetChanges]);

  const metricOptions = useMemo(() => {
    if (sparkChartOptions.length === 0) {
      return METRIC_OPTIONS;
    }
    const options: { label: string; value: Metric }[] = [];
    for (const option of sparkChartOptions) {
      const metric = option.value as Metric;
      if (refactorEnabled(mediatype ?? '')) {
        const metricOption =
          (metric in MASTER_COLUMN_MAP
            ? MASTER_COLUMN_MAP[metric as keyof typeof MASTER_COLUMN_MAP]
            : undefined) ??
          (metric in MASTER_METRIC_MAP
            ? MASTER_METRIC_MAP[metric as keyof typeof MASTER_METRIC_MAP]
            : undefined);
        if (metricOption) {
          options.push({ label: metricOption.prettyName, value: metric });
        }
      } else {
        const knownOption = METRIC_OPTIONS.find(o => o.value === metric);
        options.push(knownOption ?? { label: option.label, value: metric });
      }
    }
    return options;
  }, [sparkChartOptions, mediatype]);

  const filterPresetOptionsMetadata = useMemo(
    () =>
      filterPresets.map(preset => {
        const [hasInvalidAnalyicsDims, invalidAnalyticDimPrettyNames] = getInvalidAnalyticsDetails(
          preset,
          true,
          expAnalyticsCols,
          'filter'
        );
        return { ...preset, hasInvalidAnalyicsDims, invalidAnalyticDimPrettyNames };
      }),
    [filterPresets, expAnalyticsCols]
  );

  const tablePresetOptionsMetadata = useMemo(
    () =>
      tablePresets.map(preset => {
        const [hasInvalidAnalyicsDims, invalidAnalyticDimPrettyNames] = getInvalidAnalyticsDetails(
          preset,
          true,
          expAnalyticsCols,
          'table'
        );
        return { ...preset, hasInvalidAnalyicsDims, invalidAnalyticDimPrettyNames };
      }),
    [tablePresets, expAnalyticsCols]
  );

  const analyticsIsSource =
    Boolean(source) && source !== SourceType.PLATFORM && source !== SourceType.GOOGLE_ADS_HUB;

  const datesContainer = (
    <div className='dates-container'>
      <div className='date-picker-container'>
        <div className='date-picker-label'>Primary Period</div>
        <div className='date-picker-container'>
          <div className='date-type-toggle-container'>
            <SegmentedControlButton
              size={Size.LG}
              options={['Relative', 'Exact']}
              selectedOption={
                pagePresetChanges.dateInfo.dateType === 'exact' ? 'Exact' : 'Relative'
              }
              onChange={() =>
                setPagePresetChanges(p => {
                  const { relative_date_info: _relativeDateInfo, ...rest } = p.dateInfo;
                  return {
                    ...p,
                    dateInfo: {
                      ...rest,
                      dateType: p.dateInfo.dateType === 'relative' ? 'exact' : 'relative'
                    }
                  };
                })
              }
              design={Design.NEUTRAL}
            />
          </div>
          {pagePresetChanges.dateInfo.dateType === 'exact' ? (
            <div className='date-field-container'>
              <DateInputField
                label='Start'
                value={dateInputs.start ?? ''}
                setValue={(value: string) =>
                  setDateInputs(d =>
                    !d.end || value > d.end ? { start: value } : { ...d, start: value }
                  )
                }
              />
              <DateInputField
                label='End'
                value={dateInputs.end ?? ''}
                setValue={(value: string) =>
                  setDateInputs(d =>
                    !d.start || value < d.start ? { end: value } : { ...d, end: value }
                  )
                }
              />
            </div>
          ) : (
            <div className='date-presets-container'>
              <DPPresets
                setSelectedPreset={preset =>
                  setPagePresetChanges(p => ({
                    ...p,
                    dateInfo: { ...p.dateInfo, relative_date_info: preset }
                  }))
                }
                selectedPreset={getSelectedPresetLabel(
                  pagePresetChanges.dateInfo.relative_date_info
                )}
                setDateInputs={setDateInputs}
                setRangeErrorPreset={setRangeErrorPreset}
                startCustomPreset={startCustomPreset}
                setStartCustomPreset={setStartCustomPreset}
                endCustomPreset={endCustomPreset}
                setEndCustomPreset={setEndCustomPreset}
                rangeErrorPreset={rangeErrorPreset}
              />
            </div>
          )}
        </div>
      </div>
      {!singleDatePeriod && (
        <>
          <div className='date-picker-container'>
            <div className='date-picker-label'>Comparison Period</div>
            <div className='date-picker-container'>
              <div className='date-type-toggle-container'>
                <SegmentedControlButton
                  size={Size.LG}
                  options={['Relative', 'Exact']}
                  selectedOption={
                    pagePresetChanges.dateInfo.comparison_dateType === 'exact'
                      ? 'Exact'
                      : 'Relative'
                  }
                  onChange={() =>
                    setPagePresetChanges(p => {
                      const {
                        comparison_relative_date_info: _comparisonRelativeDateInfo,
                        ...rest
                      } = p.dateInfo;
                      return {
                        ...p,
                        dateInfo: {
                          ...rest,
                          comparison_dateType:
                            p.dateInfo.comparison_dateType === 'relative' ? 'exact' : 'relative'
                        }
                      };
                    })
                  }
                  design={Design.NEUTRAL}
                />
              </div>
              {pagePresetChanges.dateInfo.comparison_dateType === 'exact' ? (
                <div className='date-field-container'>
                  <DateInputField
                    label='Comparison Start'
                    value={comparisonDateInputs.start ?? ''}
                    setValue={(value: string) =>
                      setComparisonDateInputs(d =>
                        !d.end || value > d.end ? { start: value } : { ...d, start: value }
                      )
                    }
                  />
                  <DateInputField
                    label='Comparison End'
                    value={comparisonDateInputs.end ?? ''}
                    setValue={(value: string) =>
                      setComparisonDateInputs(d =>
                        !d.start || value < d.start ? { end: value } : { ...d, end: value }
                      )
                    }
                  />
                </div>
              ) : (
                <div className='date-presets-container'>
                  <DPPresets
                    setSelectedPreset={preset =>
                      setPagePresetChanges(p => ({
                        ...p,
                        dateInfo: { ...p.dateInfo, comparison_relative_date_info: preset }
                      }))
                    }
                    selectedPreset={getSelectedPresetLabel(
                      pagePresetChanges.dateInfo.comparison_relative_date_info
                    )}
                    setRangeErrorPreset={setCompRangeErrorPreset}
                    startCustomPreset={startCompCustomPreset}
                    setStartCustomPreset={setStartCompCustomPreset}
                    endCustomPreset={endCompCustomPreset}
                    setEndCustomPreset={setEndCompCustomPreset}
                    rangeErrorPreset={compRangeErrorPreset}
                    setDateInputs={setComparisonDateInputs}
                    comparison
                  />
                </div>
              )}
            </div>
          </div>
          <div className='date-increments-container'>
            <div className='date-increments-label'>Date Increments</div>
            <div className='date-increments-dropdown-container'>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                className='date-increments-dropdown'
                value={pagePresetChanges.dateInfo.increments ?? 'daily'}
                options={dateIncrementsOptions}
                onChange={value =>
                  setPagePresetChanges({
                    ...pagePresetChanges,
                    dateInfo: { ...pagePresetChanges.dateInfo, increments: value as DateIncrement }
                  })
                }
              />
            </div>
          </div>
        </>
      )}
    </div>
  );

  const filtersContainer = useMemo(() => {
    const filterPresetOptions = filterPresetOptionsMetadata.map(preset => {
      const { name, id, hasInvalidAnalyicsDims, invalidAnalyticDimPrettyNames } = preset;
      const [invalidPrettyDimsFromPreset, activeInvalidDimsFromOtherPreset] =
        getInvalidGranularityDetailsFromComparingPresets(
          preset,
          incompatibleTableDimsFromTableChanges,
          'filter'
        );
      const [invalidPrettyDimsFromDelivery, activeInvalidDimsFromDelivery] =
        getInvalidGranularityDetailsFromComparingPresets(
          preset,
          incompatibleFilterDimsFromDeliveryChanges,
          'filter'
        );

      const isDisabled = Boolean(
        (analyticsIsSource && hasInvalidAnalyicsDims) ||
        (invalidPrettyDimsFromPreset && activeInvalidDimsFromOtherPreset) ||
        (invalidPrettyDimsFromDelivery && activeInvalidDimsFromDelivery)
      );

      const tooltipMessage = makeInvalidDimMsg({
        prettyDims:
          analyticsIsSource && hasInvalidAnalyicsDims
            ? invalidAnalyticDimPrettyNames
            : (invalidPrettyDimsFromPreset ?? invalidPrettyDimsFromDelivery),
        isInvalidAnalyticsDim: analyticsIsSource && hasInvalidAnalyicsDims,
        conflictingTableDims: activeInvalidDimsFromOtherPreset,
        conflictingDeliveryDims: activeInvalidDimsFromDelivery,
        type: 'filter'
      });

      return {
        label: name,
        value: `${id}`,
        disabled: isDisabled,
        simpleTooltip: isDisabled ? tooltipMessage : undefined,
        simpleTooltipVerticalShift: '-55px'
      };
    });

    return (
      <div className='filters-container'>
        <div className='filter-preset-container'>
          <div className='filter-preset-label'>Filter Preset</div>
          <div className='filter-preset-dropdown-container'>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              className='filter-preset-dropdown'
              value={
                pagePresetChanges.filterInfo.filterID
                  ? `${pagePresetChanges.filterInfo.filterID}`
                  : 'None'
              }
              options={[{ label: 'None', value: 'None' }, ...filterPresetOptions]}
              onChange={value => {
                if (value === 'None') {
                  setPagePresetChanges({ ...pagePresetChanges, filterInfo: {} });
                } else {
                  setPagePresetChanges({
                    ...pagePresetChanges,
                    filterInfo: { filterID: parseInt(value, 10) }
                  });
                }
              }}
            />
          </div>
        </div>
      </div>
    );
  }, [
    pagePresetChanges,
    setPagePresetChanges,
    analyticsIsSource,
    filterPresetOptionsMetadata,
    incompatibleTableDimsFromTableChanges,
    incompatibleFilterDimsFromDeliveryChanges
  ]);

  const groupsContainer = (
    <div className='groups-container'>
      <div className='group-container'>
        <div className='group-label'>Preset Group</div>
        <div className='group-selection-container'>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            value={hasNewGroupName ? 'New Group' : (pagePresetChanges.presetGroup ?? '')}
            options={[
              ...(hasNewGroupName ? [{ label: 'New Group', value: 'New Group' }] : []),
              ...groups
            ]}
            show={showGroupsDropdownMenu}
            onChange={value => {
              setPagePresetChanges(p => ({ ...p, presetGroup: value }));
              setHasNewGroupName(false);
            }}
            onToggle={() => setShowGroupsDropdownMenu(!showGroupsDropdownMenu)}
            footer={
              !hasNewGroupName ? (
                <>
                  <MdAdd />
                  <div className='label'>Create New Group</div>
                </>
              ) : undefined
            }
            footerOnClick={() => {
              setHasNewGroupName(true);
              setShowGroupsDropdownMenu(false);
            }}
          />
          {hasNewGroupName && (
            <div className='group-form-container'>
              <Form.Control
                className='new-name-input'
                onChange={e => {
                  const newGroupName = e.target.value;
                  setPagePresetChanges(p => ({ ...p, presetGroup: newGroupName }));
                }}
                placeholder='None'
                value={pagePresetChanges.presetGroup ?? ''}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const totalBusinessContainer = (
    <div className='total-business-container'>
      <div className='kpi-funnel-container'>
        <div className='kpi-funnel-label'>KPI Funnel</div>
        <div className='kpi-funnel-dropdowns-container'>
          <div className='kpi-funnel-dropdown-container'>
            <div className='label'>Funnel</div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              value={pagePresetChanges.globalOptions?.funnelDynamicsName ?? funnelPresets[0]?.name}
              options={funnelPresets.map(preset => ({ label: preset.name, value: preset.name }))}
              onChange={value =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: { ...pagePresetChanges.globalOptions, funnelDynamicsName: value }
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Both the "Overview" (single-channel) and "Cross-Channel Overview" tabs render this identical
  // sub-container; bpm-frontend duplicated the Spark Charts block for each, this consolidates them.
  const compareMetricsSubContainer = (
    <div className='cm-container'>
      <div className='cm-label'>Compare Metrics</div>
      <div className='cm-dropdowns-container'>
        <div className='toggle'>
          <ToggleSwitch
            design={Design.NEUTRAL}
            label='Compare Period'
            checked={pagePresetChanges.globalOptions?.compareMetricsIncludeOtherDates ?? false}
            onChange={() =>
              setPagePresetChanges({
                ...pagePresetChanges,
                globalOptions: {
                  ...pagePresetChanges.globalOptions,
                  compareMetricsIncludeOtherDates: !(
                    pagePresetChanges.globalOptions?.compareMetricsIncludeOtherDates ?? false
                  )
                }
              })
            }
          />
        </div>
        {[0, 1, 2, 3].map(index => (
          <div className='cm-dropdown-container' key={`cm-dropdown-item-metric-${index}`}>
            <div className='label'>{`Metric ${index + 1}`}</div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              value={pagePresetChanges.globalOptions?.compareMetrics?.[index] ?? ''}
              options={[...metricOptions, { label: 'None', value: '' }] as DropdownOption[]}
              onChange={value => {
                const newCompareMetrics = [
                  ...(pagePresetChanges.globalOptions?.compareMetrics ?? [
                    undefined,
                    undefined,
                    undefined,
                    undefined
                  ])
                ];
                newCompareMetrics[index] = value === '' ? undefined : (value as Metric);
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: {
                    ...pagePresetChanges.globalOptions,
                    compareMetrics: newCompareMetrics
                  }
                });
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const overviewContainer = (
    <div className='overview-container'>
      <div className='spark-charts-container'>
        <div className='spark-charts-label'>Spark Charts</div>
        <div className='spark-chart-selectors-container'>
          {sparkCharts.map((chart, index) => (
            <div className='spark-chart-selector' key={`spark-chart-selector-${chart}`}>
              <div className='label'>{`Chart ${index + 1}`}</div>
              {!(
                chart.toLowerCase() === 'roas' ||
                (chart.length === 3 && chart.toLowerCase().startsWith('cp'))
              ) && (
                <SegmentedControlButton
                  design={Design.NEUTRAL}
                  options={['Total', 'Avg']}
                  selectedOption={
                    pagePresetChanges.globalOptions?.sparkChartTypes?.[index] || 'Total'
                  }
                  onChange={value => {
                    const newSparkChartTypes = pagePresetChanges.globalOptions?.sparkChartTypes
                      ? [...pagePresetChanges.globalOptions.sparkChartTypes]
                      : [false, false, false, false];
                    newSparkChartTypes[index] = value;
                    setPagePresetChanges(p => ({
                      ...p,
                      globalOptions: { ...p.globalOptions, sparkChartTypes: newSparkChartTypes }
                    }));
                  }}
                />
              )}
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                options={metricOptions}
                value={chart}
                onChange={value => {
                  const newSparkCharts = [...sparkCharts];
                  newSparkCharts[index] = value;
                  setPagePresetChanges(p => ({
                    ...p,
                    globalOptions: { ...p.globalOptions, sparkCharts: newSparkCharts }
                  }));
                }}
              />
            </div>
          ))}
        </div>
      </div>
      {compareMetricsSubContainer}
    </div>
  );

  const metricsTableContainer = useMemo(() => {
    const tableOptions = tablePresetOptionsMetadata.map(preset => {
      const { name, id, hasInvalidAnalyicsDims, invalidAnalyticDimPrettyNames } = preset;
      const [invalidPrettyDimsFromPreset, activeInvalidDimsFromOtherPreset] =
        getInvalidGranularityDetailsFromComparingPresets(
          preset,
          incompatibleFilterDimsFromFilterChanges,
          'table'
        );

      const isDisabled = Boolean(
        (analyticsIsSource && hasInvalidAnalyicsDims) ||
        (invalidPrettyDimsFromPreset && activeInvalidDimsFromOtherPreset)
      );

      const tooltipMessage = makeInvalidDimMsg({
        prettyDims:
          hasInvalidAnalyicsDims && analyticsIsSource
            ? invalidAnalyticDimPrettyNames
            : invalidPrettyDimsFromPreset,
        isInvalidAnalyticsDim: hasInvalidAnalyicsDims && analyticsIsSource,
        conflictingFilterDims: activeInvalidDimsFromOtherPreset,
        type: 'table'
      });

      return {
        label: name,
        value: `${id}`,
        disabled: isDisabled,
        simpleTooltip: isDisabled ? tooltipMessage : undefined,
        simpleTooltipVerticalShift: '-70px'
      };
    });

    return (
      <div className='metrics-table-container'>
        <div className='table-preset-container'>
          <div className='table-preset-label'>Table Preset</div>
          <div className='table-preset-dropdown-container'>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              className='table-preset-dropdown'
              value={`${pagePresetChanges.globalOptions?.tableID}`}
              options={tableOptions}
              onChange={value =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: {
                    ...pagePresetChanges.globalOptions,
                    tableID: parseInt(value, 10)
                  }
                })
              }
            />
          </div>
        </div>
      </div>
    );
  }, [
    pagePresetChanges,
    setPagePresetChanges,
    analyticsIsSource,
    tablePresetOptionsMetadata,
    incompatibleFilterDimsFromFilterChanges
  ]);

  const sourcePicker = useMemo(() => {
    if (!globalOptionsPickers.source) {
      return null;
    }
    const isRefactoredSCP = refactorEnabled(mediatype ?? '');
    let disableByPresets = false;
    let tooltiptextStr: string | undefined;
    let verticalShift = 55;
    let plural = false;

    if (isRefactoredSCP) {
      const activeTablePresetFromMetaData = tablePresetOptionsMetadata.find(
        tablePreset => tablePreset.id === pagePresetChanges.globalOptions?.tableID
      );
      const activeFilterPresetFromMetaData = filterPresetOptionsMetadata.find(
        filterPreset => filterPreset.id === pagePresetChanges.filterInfo.filterID
      );
      if (activeTablePresetFromMetaData?.hasInvalidAnalyicsDims) {
        disableByPresets = true;
        const dimNames = activeTablePresetFromMetaData.invalidAnalyticDimPrettyNames;
        const dimsPlural = dimNames.length > 1;
        verticalShift = 70;
        if (dimsPlural) {
          plural = true;
          tooltiptextStr = `Metrics table dimensions ${dimNames.join(', and ')}`;
        } else {
          tooltiptextStr = `Metrics table dimension ${dimNames.join(', and ')}`;
        }
      }
      if (activeFilterPresetFromMetaData?.hasInvalidAnalyicsDims) {
        disableByPresets = true;
        if (tooltiptextStr) {
          tooltiptextStr = `${tooltiptextStr} and `;
          verticalShift = 90;
          plural = true;
        }
        const dimNames = activeFilterPresetFromMetaData.invalidAnalyticDimPrettyNames;
        const filterMessage = `Filter with ${dimNames.join(', and ')}`;
        tooltiptextStr = `${tooltiptextStr ?? ''} ${filterMessage}`;
      }
      if (disableByPresets && tooltiptextStr) {
        const msgEnding = plural ? 'are incompatible with SoT' : 'is incompatible with SoT';
        tooltiptextStr = `${tooltiptextStr} ${msgEnding}`;
      }
    }

    const sourceOptionsParsed = globalOptionsPickers.source.map(sourceOption => {
      const isPlatform = sourceOption.value === 'platform';
      return {
        label: sourceOption.label,
        value: sourceOption.value,
        disabled: !isPlatform && disableByPresets,
        simpleTooltip: tooltiptextStr,
        simpleTooltipVerticalShift: `-${verticalShift}px`
      };
    });

    return (
      <Dropdown
        overlayPlacement={Placement.RIGHT}
        label='Source'
        onChange={option =>
          setPagePresetChanges({
            ...pagePresetChanges,
            globalOptions: { ...pagePresetChanges.globalOptions, source: option }
          })
        }
        options={sourceOptionsParsed}
        value={source ?? 'platform'}
      />
    );
  }, [
    globalOptionsPickers.source,
    mediatype,
    pagePresetChanges,
    setPagePresetChanges,
    source,
    filterPresetOptionsMetadata,
    tablePresetOptionsMetadata
  ]);

  const globalOptionsContainer = (
    <div className='global-options-container'>
      {globalOptionsPickers.kpi?.options && (
        <div className='global-options-dropdown-container'>
          <div className='global-options-label'>KPI</div>
          <div className='global-options-dropdown'>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              label='KPI'
              onChange={option =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: { ...pagePresetChanges.globalOptions, kpi: option }
                })
              }
              options={globalOptionsPickers.kpi.options}
              value={kpi}
            />
          </div>
        </div>
      )}
      {globalOptionsPickers.lag && (
        <div className='global-options-dropdown-container'>
          <div className='global-options-label'>Lag Window</div>
          <div className='global-options-dropdown'>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              label='Lag'
              onChange={option =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: {
                    ...pagePresetChanges.globalOptions,
                    lag: option as GlobalOptions['lag']
                  }
                })
              }
              options={globalOptionsPickers.lag}
              value={lag ?? '7d'}
            />
          </div>
        </div>
      )}
      {globalOptionsPickers.audience && (
        <div className='global-options-dropdown-container'>
          <div className='global-options-label'>Audience</div>
          <div className='global-options-dropdown'>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              label='Audience'
              onChange={option =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: {
                    ...pagePresetChanges.globalOptions,
                    audience: option as GlobalOptions['audience']
                  }
                })
              }
              options={globalOptionsPickers.audience}
              value={audience ?? 'HH'}
            />
          </div>
        </div>
      )}
      {sourcePicker && (
        <div className='global-options-dropdown-container'>
          <div className='global-options-label'>Source</div>
          <div className='global-options-dropdown'>{sourcePicker}</div>
        </div>
      )}
      {globalOptionsPickers.audience && (
        <div className='global-options-dropdown-container'>
          <div className='global-options-label'>Use Unequivalized Impressions?</div>
          <div className='global-options-dropdown'>
            <ToggleSwitch
              label='Use Unequivalized Impressions?'
              checked={Boolean(shouldUseUnequivalizedImpressions)}
              onChange={() =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: {
                    ...pagePresetChanges.globalOptions,
                    shouldUseUnequivalizedImpressions: !shouldUseUnequivalizedImpressions
                  }
                })
              }
            />
          </div>
        </div>
      )}
      {Boolean(shouldEnableByResponseDate) &&
        Boolean(globalOptionsPickers.impsBetween) &&
        Boolean(shouldEnableImpsBetweenToggle) && (
          <div className='global-options-dropdown-container'>
            <div className='global-options-label'>Imps Between</div>
            <div className='global-options-dropdown'>
              <SegmentedControlButton
                size={Size.LG}
                options={['Imps Between', 'Response Between']}
                selectedOption={impsBetween ? 'Imps Between' : 'Response Between'}
                onChange={() =>
                  setPagePresetChanges({
                    ...pagePresetChanges,
                    globalOptions: { ...pagePresetChanges.globalOptions, impsBetween: !impsBetween }
                  })
                }
              />
            </div>
          </div>
        )}
    </div>
  );

  const deliveryContainer = (
    <div className='delivery-container'>
      <div className='delivery-chart-container'>
        <div className='delivery-label'>Delivery Options</div>
        <div className='delivery-options-container'>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            options={deliveryMetricOptions.map(option => ({
              label: option.label,
              value: option.label
            }))}
            value={
              pagePresetChanges.globalOptions?.deliveryMetric ?? deliveryMetricOptions[0].label
            }
            onChange={value =>
              setPagePresetChanges({
                ...pagePresetChanges,
                globalOptions: { ...pagePresetChanges.globalOptions, deliveryMetric: value }
              })
            }
          />
          <div className='delivery-option'>
            <div className='label'>Delivery by</div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              options={Object.keys(deliveryDimensionMap).map(key => {
                const conflictingDims = incompatibleDeliveryDimsFromFilterChanges[key];
                return {
                  label: key,
                  value: key,
                  disabled: Boolean(conflictingDims?.length),
                  simpleTooltip: conflictingDims?.length
                    ? makeInvalidDimMsg({
                        prettyDims: [key],
                        isInvalidAnalyticsDim: false,
                        conflictingFilterDims: conflictingDims
                      })
                    : undefined,
                  simpleTooltipVerticalShift: '-55px'
                };
              })}
              value={
                pagePresetChanges.globalOptions?.deliveryDimension ??
                Object.keys(deliveryDimensionMap)[0]
              }
              onChange={value =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: { ...pagePresetChanges.globalOptions, deliveryDimension: value }
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );

  const deliveryAndPerformanceContainer = (
    <div className='dp-container'>
      <div className='dp-item-container'>
        <div className='dp-label'>Spend vs Budget Snapshot</div>
        <div className='dp-item-dropdowns-container'>
          <div className='dp-item-dropdown-container'>
            <div className='label'>Top</div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              value={pagePresetChanges.globalOptions?.sbSnapshotTop}
              options={makeDropdownOptions().topDropdownOptions}
              onChange={value =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: { ...pagePresetChanges.globalOptions, sbSnapshotTop: value }
                })
              }
            />
          </div>
          <div className='dp-item-dropdown-container'>
            <div className='label'>Bottom</div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              value={pagePresetChanges.globalOptions?.sbSnapshotBottom}
              options={makeDropdownOptions().bottomDropdownOptions}
              onChange={value =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: { ...pagePresetChanges.globalOptions, sbSnapshotBottom: value }
                })
              }
            />
          </div>
        </div>
      </div>
      <div className='dp-item-container'>
        <div className='dp-label'>Delivery Over Time</div>
        <div className='dp-item-dropdowns-container'>
          <div className='toggle'>
            <ToggleSwitch
              design={Design.NEUTRAL}
              label='Compare Period'
              checked={
                pagePresetChanges.globalOptions?.crossChannelDeliveryIncludeOtherDates ?? false
              }
              onChange={() =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: {
                    ...pagePresetChanges.globalOptions,
                    crossChannelDeliveryIncludeOtherDates: !(
                      pagePresetChanges.globalOptions?.crossChannelDeliveryIncludeOtherDates ??
                      false
                    )
                  }
                })
              }
            />
          </div>
          <div className='dp-item-dropdown-container'>
            <div className='label'>Metric</div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              value={pagePresetChanges.globalOptions?.crossChannelDeliveryMetric}
              options={DELIVERY_METRIC_OPTIONS}
              onChange={value =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: {
                    ...pagePresetChanges.globalOptions,
                    crossChannelDeliveryMetric: value
                  }
                })
              }
            />
          </div>
        </div>
      </div>
      <div className='dp-item-container'>
        <div className='dp-label'>Performance Snapshot</div>
        <div className='dp-item-dropdowns-container'>
          <div className='dp-item-dropdown-container'>
            <div className='label'>Metric</div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              value={pagePresetChanges.globalOptions?.crossChannelPerformanceSnapshotMetric}
              options={PERFORMANCE_METRIC_OPTIONS}
              onChange={value =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: {
                    ...pagePresetChanges.globalOptions,
                    crossChannelPerformanceSnapshotMetric: value
                  }
                })
              }
            />
          </div>
        </div>
      </div>
      <div className='dp-item-container'>
        <div className='dp-label'>Performance Over Time</div>
        <div className='dp-item-dropdowns-container'>
          <div className='toggle'>
            <ToggleSwitch
              design={Design.NEUTRAL}
              label='Compare Period'
              checked={
                pagePresetChanges.globalOptions?.crossChannelPerformanceIncludeOtherDates ?? false
              }
              onChange={() =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: {
                    ...pagePresetChanges.globalOptions,
                    crossChannelPerformanceIncludeOtherDates: !(
                      pagePresetChanges.globalOptions?.crossChannelPerformanceIncludeOtherDates ??
                      false
                    )
                  }
                })
              }
            />
          </div>
          <div className='dp-item-dropdown-container'>
            <div className='label'>Metric</div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              value={pagePresetChanges.globalOptions?.crossChannelPerformanceMetric}
              options={PERFORMANCE_METRIC_OPTIONS}
              onChange={value =>
                setPagePresetChanges({
                  ...pagePresetChanges,
                  globalOptions: {
                    ...pagePresetChanges.globalOptions,
                    crossChannelPerformanceMetric: value
                  }
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Modal centered className='edit-page-preset-modal' onHide={onHide} show size='lg'>
      <ModalHeader
        pagePresetChanges={pagePresetChanges}
        newName={newName}
        setNewName={setNewName}
        adminLock={adminLock}
        isAdmin={isAdmin}
        internalOnly={internalOnly}
        setPagePresetChanges={setPagePresetChanges}
      />
      <Modal.Body>
        <TabSelector
          menuTabs={menuTabs}
          presetGroup={pagePresetChanges.presetGroup}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
        {(selectedTab === 'Cross-Channel Overview' || selectedTab === 'Overview') &&
          overviewContainer}
        {selectedTab === 'Dates' && datesContainer}
        {selectedTab === 'Delivery & Performance' && deliveryAndPerformanceContainer}
        {selectedTab === 'Delivery' && deliveryContainer}
        {selectedTab === 'Filters' && filtersContainer}
        {selectedTab === 'KPI, etc.' && globalOptionsContainer}
        {selectedTab === 'Metrics Table' && metricsTableContainer}
        {selectedTab === 'Preset Group' && groupsContainer}
        {selectedTab === 'Total Business' && totalBusinessContainer}
        {extraTabs.find(tab => tab.label === selectedTab)?.content}
      </Modal.Body>
      <ModalFooter
        disableApply={disableApply}
        newName={newName}
        onHide={onHide}
        pagePresetChanges={pagePresetChanges}
        saveAndApplyOnClick={saveAndApplyOnClick}
      />
    </Modal>
  );
};
